import { useState, useEffect } from 'react';
import { Button, Checkbox, Stack, Text, TagsInput, Select, Textarea } from '@mantine/core';
import { IconMailForward, IconReportMoney } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import CardModal from './card-modal';
import { useSendReservationSettlementEmail } from '@/hooks/api-hooks';
import { Reservation } from '@turizi/shared';
import { localTenant } from '@/services/session-service';

interface Props {
    opened: boolean;
    onClose: () => void;
    reservation?: Reservation;
}

export default function SendTripEmailModal({ opened, onClose, reservation }: Props): JSX.Element {
    const { t } = useTranslation();

    const tenant = localTenant();
    const senderEmails = Array.from(
        new Set([
            tenant?.settings?.email,
            ...(tenant?.settings?.sender_emails || [])
        ].filter(Boolean))
    );
    const [selectedFromEmail, setSelectedFromEmail] = useState<string>(senderEmails[0] || '');

    useEffect(() => {
        if (senderEmails.length > 0 && !selectedFromEmail) {
            setSelectedFromEmail(senderEmails[0]);
        }
    }, [senderEmails]);

    // Extract existing email addresses safely
    const [existingEmails, setExistingEmails] = useState<string[]>([]);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [manualEmails, setManualEmails] = useState<string[]>([]);
    const [customMessage, setCustomMessage] = useState<string>('');

    useEffect(() => {
        if (reservation) {
            const emailsSet = new Set<string>();

            // From client_business
            if (reservation.client_business?.emails) {
                Object.values(reservation.client_business.emails).forEach((email) => {
                    if (email && typeof email === 'string') {
                        emailsSet.add(email.trim().toLowerCase());
                    }
                });
            }

            // From pax groups (coordinator, persons)
            if (reservation.pax_groups) {
                reservation.pax_groups.forEach((group) => {
                    if (group.coordinator?.email) {
                        emailsSet.add(group.coordinator.email.trim().toLowerCase());
                    }
                    if (group.persons) {
                        group.persons.forEach((person) => {
                            if (person.email) {
                                emailsSet.add(person.email.trim().toLowerCase());
                            }
                        });
                    }
                });
            }

            const list = Array.from(emailsSet);
            setExistingEmails(list);
        } else {
            setExistingEmails([]);
        }
        setSelectedEmails([]);
        setManualEmails([]);
        setCustomMessage('');
    }, [reservation, opened]);

    const sendMutation = useSendReservationSettlementEmail();

    const handleSend = () => {
        if (!reservation?.id) return;
        const allEmails = [...selectedEmails, ...manualEmails].filter(Boolean);
        if (allEmails.length === 0) return;

        sendMutation.mutate({
            id: reservation.id,
            emails: allEmails,
            fromEmail: selectedFromEmail,
            customMessage: customMessage.trim() || undefined
        }, {
            onSuccess: () => {
                onClose();
            }
        });
    };

    const hasAnyEmail = existingEmails.length > 0;

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            header={
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-slate-100 text-slate-800">
                        <IconReportMoney size={18} />
                    </div>
                    <div className="font-bold text-lg text-slate-800">
                        {t('send_settlement')}
                    </div>
                </div>
            }
            size="md"
            footer={
                <div className="flex justify-end gap-2">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={onClose}
                        disabled={sendMutation.isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        color="black"
                        leftSection={<IconMailForward size={16} />}
                        onClick={handleSend}
                        loading={sendMutation.isPending}
                        disabled={selectedEmails.length === 0 && manualEmails.length === 0}
                    >
                        {t('send')}
                    </Button>
                </div>
            }
        >
            <Stack className="space-y-2">
                {/* Sender Email Select */}
                {senderEmails.length > 0 && (
                    <Select
                        label={t('select_sender_email')}
                        data={senderEmails}
                        value={selectedFromEmail}
                        onChange={(val) => setSelectedFromEmail(val || '')}
                        allowDeselect={false}
                    />
                )}

                <div>
                    {hasAnyEmail ? (
                        <div className="space-y-2">
                            {existingEmails.map(email => (
                                <Checkbox
                                    key={email}
                                    label={email}
                                    checked={selectedEmails.includes(email)}
                                    onChange={(event) => {
                                        if (event.currentTarget.checked) {
                                            setSelectedEmails(prev => [...prev, email]);
                                        } else {
                                            setSelectedEmails(prev => prev.filter(e => e !== email));
                                        }
                                    }}
                                    color="black"
                                />
                            ))}
                        </div>
                    ) : (
                        <Text className="text-xs text-gray-400 italic">{t('no_emails_available')}</Text>
                    )}
                </div>

                <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">
                        {t('enter_additional_emails')}
                    </div>
                    <TagsInput
                        placeholder="example@mail.com"
                        value={manualEmails}
                        onChange={setManualEmails}
                        clearable
                    />
                </div>

                <div>
                    <div className="text-xs font-medium text-gray-700 mb-1">
                        {t('custom_message_optional')}
                    </div>
                    <Textarea
                        placeholder={t('custom_message_placeholder')}
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.currentTarget.value)}
                        minRows={3}
                        maxRows={6}
                        autosize
                    />
                </div>
            </Stack>
        </CardModal>
    );
}
